import { UPSELLS_ENABLED } from "@/lib/features";
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { trackBrevo, touchActivity } from "@/lib/brevo";

import type { Plan } from "@/lib/plans";
export type { Plan };
export type BillingInterval = "month" | "year";

export interface Subscription {
  plan: Plan;
  billingInterval: BillingInterval | null;
  status: string | null;
  currentPeriodEnd: string | null;
  /** True for any paid tier (Pro or Ultra). */
  isPro: boolean;
  isUltra: boolean;
  /** Never subscribed before → eligible for the $1.99 Pro intro month. */
  promoEligible: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useSubscription(): Subscription {
  const [plan, setPlan] = useState<Plan>("free");
  const [billingInterval, setBillingInterval] = useState<BillingInterval | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<string | null>(null);
  const [promoEligible, setPromoEligible] = useState(false);
  const [loading, setLoading] = useState(true);
  const previousPlanRef = useRef<Plan | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        setPlan("free");
        return;
      }
      const { data, error } = await supabase.functions.invoke<{
        plan: Plan;
        billingInterval: BillingInterval | null;
        status: string | null;
        currentPeriodEnd: string | null;
        promoEligible?: boolean;
      }>("check-subscription", { body: {} });
      if (error || !data) return;
      const nextPlan = data.plan ?? "free";
      // Free -> Pro transition = "pro_unlocked" event. Dedupe with previous ref
      // so re-refreshes don't refire.
      if (previousPlanRef.current === "free" && nextPlan !== "free") {
        void trackBrevo("pro_unlocked", {
          billing_interval: data.billingInterval ?? null,
        });
        touchActivity();
      }
      previousPlanRef.current = nextPlan;
      setPlan(nextPlan);
      setBillingInterval(data.billingInterval);
      setStatus(data.status);
      setCurrentPeriodEnd(data.currentPeriodEnd);
      setPromoEligible(UPSELLS_ENABLED && nextPlan === "free" && !!data.promoEligible);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    plan,
    billingInterval,
    status,
    currentPeriodEnd,
    isPro: plan !== "free",
    isUltra: plan === "ultra",
    promoEligible,
    loading,
    refresh,
  };
}
